import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateBreadCrumbs, usePagination } from '../../hooks';
import { Button, IconButton, Loading, OrganizationCard, Status } from '../../uiComponents/common';
import { useQuery } from 'react-query';
import { WelletContext } from '../../context/wellet/welletContext';
import { PageHeader, Table, TableCell, Show, FilterCard } from '../../components';
import { getSpecialDates } from '../../services';
import { formatDateTranslation } from '../../helpers/dates';
import { FwTrashIcon, PencilIcon } from '../../uiComponents/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { defaultQueryOptions } from '../../utils/defaultQueryOptions';
import DeleteEventModal from './modals/deleteEventModal';
import './fechasEspeciales.css';

const getStatusProps = (status) => {
  switch (status) {
    case 'active':
      return { status: true, color: 'green' };
    case 'upcoming':
      return { status: 'yellow', color: 'yellow' };
    case 'finished':
      return { status: false, color: 'red' };
    case 'isdeleted':
      return { status: false, color: 'grey' };
    default:
      return { status: false, color: 'grey' };
  }
};

const FechasEspeciales = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'screens.fechasEspeciales' });
  const welletContext = useContext(WelletContext);
  const navigate = useNavigate();
  const organization = useSelector((state) => state.app.selectedOrganization);
  const language = useSelector((state) => state.app.language);
  const [deleteId, setDeleteId] = useState('');
  const [filter, setFilter] = useState('');

  const { currentPage, totalItems, handlePageChange, setTotalAndReset } = usePagination();
  let limit = 10;
  let skip = 10 * ((currentPage ? currentPage : 1) - 1);

  const marketId = organization?.marketId;
  const organizationId =
    organization.id === organization.marketId ? null : Number(organization.id);

  const statusMap = { '': 'All', 'ACTIVE': 'Active', 'UPCOMING': 'Upcoming', 'FINISHED': 'Finished', 'ISDELETED': 'IsDeleted' };

  const specialDatesQuery = useQuery({
    queryKey: ['specialDates', marketId, organizationId, filter, limit, skip],
    queryFn: () =>
      getSpecialDates(welletContext, marketId, organizationId, {
        limit,
        skip,
        status: statusMap[filter] || 'All',
      }),
    ...defaultQueryOptions,
    enabled: !!marketId,
    onSuccess: (data) => {
      const pagination = data?.data?.pagination || data?.data;
      setTotalAndReset(pagination?.totalRegisters ?? pagination?.items?.length ?? 0);
    },
  });

  const responseData = specialDatesQuery?.data?.data;
  const pagination = responseData?.pagination || responseData;
  const counters = responseData?.counters;
  const items = pagination?.items ?? [];
  const totalCount = pagination?.totalRegisters ?? items.length;

  const activeCount = counters?.active ?? 0;
  const upcomingCount = counters?.upcoming ?? 0;
  const finishedCount = counters?.finished ?? 0;
  const isDeletedCount = counters?.isDeleted ?? 0;

  const headerAdmin = [
    { title: t('table.name'), value: '', filterable: false },
    { title: t('table.restaurant'), value: '', filterable: false },
    { title: t('table.dateFrom'), value: '', filterable: false },
    { title: t('table.dateTo'), value: '', filterable: false },
    { title: t('table.status'), value: '', filterable: false },
    { title: '', value: '', filterable: false },
  ];

  useCreateBreadCrumbs([
    { name: t('title'), path: '/special-dates' },
  ]);

  const handleFilter = (value) => {
    setFilter(value);
    handlePageChange(1);
  };

  const handleRowClick = (item) => {
    navigate(`/special-dates/${item.id}`);
  };

  return (
    <div>
      {specialDatesQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <PageHeader
            title={t('title')}
            marginBottom={false}
            endComponent={
              <Button
                size='medium'
                className='btn-wellet-secondary'
                text={t('btnCreate')}
                onClick={() => navigate('/special-dates/new')}
              />
            }
          />
          <div className='row px-1 my-3'>
            <FilterCard
              category={t('tabs.all')}
              quantity={counters?.all ?? totalCount}
              onClick={() => handleFilter('')}
              className='col'
              isSelected={filter === ''}
            />
            <FilterCard
              category={t('tabs.active')}
              quantity={activeCount}
              onClick={() => handleFilter('ACTIVE')}
              className='col'
              isSelected={filter === 'ACTIVE'}
            />
            <FilterCard
              category={t('tabs.upcoming')}
              quantity={upcomingCount}
              onClick={() => handleFilter('UPCOMING')}
              className='col'
              isSelected={filter === 'UPCOMING'}
            />
            <FilterCard
              category={t('tabs.finished')}
              quantity={finishedCount}
              onClick={() => handleFilter('FINISHED')}
              className='col'
              isSelected={filter === 'FINISHED'}
            />
            <FilterCard
              category={t('tabs.isDeleted')}
              quantity={isDeletedCount}
              onClick={() => handleFilter('ISDELETED')}
              className='col'
              isSelected={filter === 'ISDELETED'}
            />
          </div>
          <Table
              data={items}
              currentPage={currentPage}
              header='grey'
              hasPagination={totalCount > limit}
              onPageChange={(page) => handlePageChange(page)}
              totalP={totalCount}
              columns={headerAdmin}
              onRowClick={(item) => {
                const s = (item.status || '').toLowerCase();
                if (s !== 'isdeleted') handleRowClick(item);
              }}
              renderRow={(item) => {
                const status = (item.status || '').toLowerCase();
                const statusProps = getStatusProps(status);
                const { date: fromDate } = formatDateTranslation(item.fromDate, language, 'small');
                const { date: toDate } = formatDateTranslation(item.toDate, language, 'small');

                return (
                  <>
                    <TableCell className='fw-700'>{item.name}</TableCell>
                    <TableCell>
                      <OrganizationCard
                        name={item.simpleRestaurant?.name}
                        date={item.simpleRestaurant?.cityName}
                        size={40}
                        imageUrl={item.simpleRestaurant?.logo}
                        hightLightName={true}
                      />
                    </TableCell>
                    <TableCell>
                      <span className='sentences-capitalize'>{fromDate}</span>
                    </TableCell>
                    <TableCell>
                      <span className='sentences-capitalize'>{toDate}</span>
                    </TableCell>
                    <TableCell style={{ width: '120px' }}>
                      <Status status={statusProps.status} text={t(`status.${status}`)} />
                    </TableCell>
                    <TableCell style={{ width: '80px' }} type='primary'>
                      {status !== 'isdeleted' && (
                        <div
                          className='d-flex align-items-center justify-content-center'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            icon={<PencilIcon size='20px' />}
                            onClick={() => navigate(`/special-dates/${item.id}`)}
                          />
                          <IconButton
                            icon={<FwTrashIcon size='20px' />}
                            onClick={() => setDeleteId(item.id)}
                          />
                        </div>
                      )}
                    </TableCell>
                  </>
                );
              }}
            />
          <DeleteEventModal
            eventId={deleteId}
            open={deleteId !== ''}
            setOpen={setDeleteId}
            refetchQuery={specialDatesQuery.refetch}
          />
        </>
      )}
    </div>
  );
};

export default FechasEspeciales;
